import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TypeCurveStatusTag } from 'components/lifecycle-status-tag';
import { useParams } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from 'components/page-header';
import { ExportButtons } from './export-buttons';
import { pageTitle } from 'utils/page-title';
import style from './type-curves-details.module.scss';
import { Loading, Tile, Layer, InlineNotification } from '@carbon/react';
import DevAreaSvg from 'images/srp-development-areas.svg';
import {
  typeCurveHeaderCommentsHref,
  typeCurveHeaderEventsHref,
  typeCurveHeaderWorkspacesCollectionHref,
  typeCurveHeaderWorkspacesKindsHref,
} from 'types/type-curve-api-hrefs';
import {
  workspacesFromListPayload,
  tcWorkspaceSelfHref,
  tcWorkspaceUnitResolutionEventsHref,
  getTcWorkspaceUnitResolutionVerification,
  getTcWorkspaceInventoryVerification,
  type TypeCurveWorkspaceResponse,
} from 'types/type-curve-workspace';
import { get as getReq } from 'lib/request/request';
import { verificationItemEventMutationAtom } from 'components/verification-controls/atoms';
import { EmptyState } from 'components/empty-state';
import { TypeCurveDetailsContent } from './type-curve-details-content';
import { TypeCurvePageFilters } from './type-curve-filters/type-curve-page-filters';
import { SAVED_WELL_FILTERS_KEY } from './type-curve-filters/use-saved-well-filters';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import {
  DualSidebarLayout,
  SidebarToggle,
} from 'components/layout/dual-sidebar-layout';
import { TcWellFilterModal } from './type-curve-filters/tc-well-filter-modal';
import { TcUnitFilterModal } from './type-curve-filters/tc-unit-filter-modal';
import { TcColorByModal } from './type-curve-filters/tc-color-by-modal';
import { TcUnitColorByModal } from './type-curve-filters/tc-unit-color-by-modal';
import { TcCrossPlotChartModal } from './type-curve-filters/tc-cross-plot-chart-modal';
import { CreateMenu } from 'components/type-curve-workspace/create-menu';
import { CreateTypeCurveWorkspaceModal } from 'components/type-curve-workspace/create-type-curve-workspace-modal';
import { CreateInventoryWorkspaceModal } from 'components/type-curve-workspace/create-inventory-workspace-modal';
import {
  activeTcWorkspaceAtom,
  latestPromotedWorkspaceIdAtom,
  promoteTypeCurveWorkspaceAtom,
  tcDetailsRightPanelAtom,
  useEffectiveTypeCurveHeader,
  useTcWorkspacesListResourceAtom,
} from 'components/type-curve-workspace/atoms';
import { TypeCurveWorkspaceHeader } from 'components/type-curve-workspace/type-curve-workspace-header';
import { TcPromoteWorkspaceModal } from 'components/type-curve-workspace/tc-promote-workspace-modal';
import { TcVerificationDecisionPanel } from 'components/type-curve-workspace/tc-verification-decision-panel';
import {
  TcVerificationTasksPanel,
  initializeTasksAtom,
  useWorkspaceTasksQuery,
} from 'components/type-curve-workspace/tasks';
import { UnitResolutionPanel } from 'components/type-curve-workspace/unit-resolution/unit-resolution-panel';
import { TcAreaSelectorPanel } from 'components/type-curve-workspace/tc-area-selector-panel';
import { InventoryGuideModal } from 'components/type-curve-workspace/unit-resolution/inventory-guide-modal';
import { TcWorkspacesListPanel } from 'components/type-curve-workspace/tc-workspaces-list-panel';
import {
  WorkspacesMenu,
  type WorkspacesMenuType,
} from 'components/type-curve-workspace/workspaces-menu';
import { DcaScenarioSelection } from './dca-scenario-selection';
import { savedDcaScenarioId, useSeedDcaScenario } from 'lib/dca-scenarios';
import { selectedDcaScenarioAtom } from 'components/well-page-content/atoms';
import { TypeCurveVerificationStatusIndicator } from 'components/type-curve-verification-status/type-curve-verification-status';
import { TypeCurveValidationCell } from 'components/type-curve-validation-status/type-curve-validation-status';
import { CheckmarkFilled } from '@carbon/react/icons';
import {
  tcFilteredUwisAtom,
  tcWellsTotalCountAtom,
  tcUnitsTotalCountAtom,
  removedUnitIdsAtom,
  tcOverlappingUnitsAtom,
  tcHasTypeCurveAreaAtom,
  effectivePageSectionVisibilityAtom,
} from './type-curve-filters/atoms';
import {
  toastAtom,
  inventoryVerificationSummaryAtom,
  tcDetailsDraftAtom,
} from './atoms';
import { tcDcaFitParamsAtom } from './type-curve-dca/dca-fit-atoms';

const TypeCurveDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const [leftOpen, setLeftOpen] = useState(true);
  const [editingColorRuleId, setEditingColorRuleId] = useState<string | null>(
    null
  );
  const [editingUnitColorRuleId, setEditingUnitColorRuleId] = useState<
    string | null
  >(null);
  const wellsTotalCount = useAtomValue(tcWellsTotalCountAtom);
  const unitsTotalCount = useAtomValue(tcUnitsTotalCountAtom);
  const filteredUwis = useAtomValue(tcFilteredUwisAtom);
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const activeTcWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const setActiveTcWorkspace = useSetAtom(activeTcWorkspaceAtom);
  const [rightPanel, setRightPanel] = useAtom(tcDetailsRightPanelAtom);
  const [createTcWorkspaceModalOpen, setCreateTcWorkspaceModalOpen] =
    useState(false);
  const [
    createInventoryWorkspaceModalOpen,
    setCreateInventoryWorkspaceModalOpen,
  ] = useState(false);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  // Set as Default travels with the promote and nothing else. Reset whenever the modal opens, so a
  // previous session's tick can never ride along on the next promote unnoticed.
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [promoteReasons, setPromoteReasons] = useState<string[]>([]);
  const setToast = useSetAtom(toastAtom);
  const [{ loading: promoteLoading, result: promoteResult }, promoteWorkspace] =
    useAtom(promoteTypeCurveWorkspaceAtom);
  const inventoryVerificationSummary = useAtomValue(
    inventoryVerificationSummaryAtom
  );
  const removedUnitIds = useAtomValue(removedUnitIdsAtom);
  const setLatestPromotedWorkspaceId = useSetAtom(
    latestPromotedWorkspaceIdAtom
  );
  const overlappingUnits = useAtomValue(tcOverlappingUnitsAtom);
  const [, submitVerificationEvent] = useAtom(
    verificationItemEventMutationAtom
  );
  const [workspaceIdReady, setWorkspaceIdReady] = useState(false);
  const initializeTasks = useSetAtom(initializeTasksAtom);
  useWorkspaceTasksQuery();
  const setDraft = useSetAtom(tcDetailsDraftAtom);
  const setHasTypeCurveArea = useSetAtom(tcHasTypeCurveAreaAtom);
  const sectionVisibility = useAtomValue(effectivePageSectionVisibilityAtom);
  const dcaFitParams = useAtomValue(tcDcaFitParamsAtom);
  const queryClient = useQueryClient();

  // Reset readiness when navigating to a different type curve so the new
  // type curve's workspace state is resolved before APIs fire.
  useEffect(() => {
    setWorkspaceIdReady(false);
  }, [id]);

  // `typeCurveData` = promoted header (page-level identity/context); `detailsData`
  // = effective header (active workspace's when in a workspace, else promoted) —
  // used for the content + color-by modals. See useEffectiveTypeCurveHeader.
  const {
    promoted: typeCurveData,
    data: detailsData,
    loading,
    error,
    refresh: refreshTypeCurveHeader,
  } = useEffectiveTypeCurveHeader(id);

  // The page starts on the Type Curve Scenario's Well Control DCA Scenario.
  const typeCurveDcaScenarioId = (detailsData ?? typeCurveData)?.dcaScenario
    ?.id;
  useSeedDcaScenario(typeCurveDcaScenarioId, id);

  // Sync the latest promoted workspace ID from the header into the atom so
  // read-only queries (map, tables, gun barrel) can use it as a fallback
  // when no active workspace is set.
  // Guard: only update when typeCurveData is present. During a refresh
  // (e.g. after leaving a workspace) the resource atom clears data to
  // undefined — without this guard we'd wipe the promoted ID to null,
  // causing downstream queries to fire without a workspace ID.
  useEffect(() => {
    if (typeCurveData) {
      setLatestPromotedWorkspaceId(
        typeCurveData.latestPromotedWorkspace?.id ?? null
      );
      setWorkspaceIdReady(true);
    }
  }, [
    typeCurveData?.latestPromotedWorkspace?.id,
    setLatestPromotedWorkspaceId,
    typeCurveData,
  ]);

  // Empty-state flag from the effective header. A promoted type curve always
  // has an area, so this is effectively always `true` here; kept for uniform
  // behavior across the workspace + details pages. `null` while loading.
  useEffect(() => {
    setHasTypeCurveArea(
      detailsData ? detailsData.typeCurveAreaId !== null : null
    );
  }, [detailsData, setHasTypeCurveArea]);

  // Reset on unmount so navigating away doesn't leave stale data.
  useEffect(() => {
    return () => {
      setLatestPromotedWorkspaceId(null);
      setHasTypeCurveArea(null);
    };
  }, [setLatestPromotedWorkspaceId, setHasTypeCurveArea]);

  // The bare collection href drives create-workspace (POST target + gate). The listing dropdown uses
  // the same collection with `?kinds=`: one call fetches every kind (the backend returns a flat
  // `workspaces` array with each row naming its own `kind`), grouped into the three sections below.
  const workspacesCollectionHref =
    typeCurveHeaderWorkspacesCollectionHref(typeCurveData);
  const workspacesKindsHref = typeCurveHeaderWorkspacesKindsHref(typeCurveData);
  const tcWorkspacesListAtom =
    useTcWorkspacesListResourceAtom(workspacesKindsHref);
  const [workspacesListResource, refreshWorkspaces] =
    useAtom(tcWorkspacesListAtom);

  const workspacesByKind = useMemo(() => {
    const rows = workspacesFromListPayload(workspacesListResource.data);
    return {
      type_curve: rows.filter((w) => w.kind === 'type_curve'),
      inventory: rows.filter((w) => w.kind === 'inventory'),
      dca: rows.filter((w) => w.kind === 'dca'),
    };
  }, [workspacesListResource.data]);
  const workspaceListCount = workspacesByKind.type_curve.length;
  const inventoryWorkspaceListCount = workspacesByKind.inventory.length;
  const dcaWorkspaceListCount = workspacesByKind.dca.length;

  const showWorkspaceIdleBanner =
    !activeTcWorkspace &&
    Boolean(typeCurveData?.id) &&
    !workspacesListResource.loading &&
    workspaceListCount === 0;

  const showDcaUnavailableBanner =
    Boolean(detailsData) &&
    sectionVisibility.dcaParameters &&
    !detailsData?.dcaForecastParameters?.length &&
    Object.keys(dcaFitParams).length === 0;

  useEffect(() => {
    if (promoteResult?.type === 'fatal') {
      setPromoteError(promoteResult.message || 'Unable to promote workspace.');
    }
  }, [promoteResult]);

  // Initialize tasks when workspace changes
  useEffect(() => {
    if (!activeTcWorkspace) return;
    const urStatus =
      getTcWorkspaceUnitResolutionVerification(activeTcWorkspace)?.status ??
      null;
    const ivStatus =
      getTcWorkspaceInventoryVerification(activeTcWorkspace)?.status ?? null;
    initializeTasks({
      unitResolutionStatus: urStatus,
      inventoryVerificationStatus: ivStatus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTcWorkspace?.id, initializeTasks]);

  // Initialize the draft from the workspace header when it loads
  useEffect(() => {
    if (!typeCurveData || !activeTcWorkspace || activeTcWorkspace.promoted) {
      setDraft(null);
      return;
    }
    setDraft({
      status: typeCurveData.status ?? '',
      lateralLength:
        typeCurveData.lateralLengthFt !== null &&
        typeCurveData.lateralLengthFt !== undefined
          ? String(typeCurveData.lateralLengthFt)
          : '',
      formation: typeCurveData.formation ?? '',
      wellSpacing:
        typeCurveData.wellSpacingFt !== null &&
        typeCurveData.wellSpacingFt !== undefined
          ? String(typeCurveData.wellSpacingFt)
          : '',
      proppantIntensity:
        typeCurveData.proppantIntensityLbsFt !== null &&
        typeCurveData.proppantIntensityLbsFt !== undefined
          ? String(typeCurveData.proppantIntensityLbsFt)
          : '',
      fluidIntensityGal:
        typeCurveData.fluidIntensityGalFt !== null &&
        typeCurveData.fluidIntensityGalFt !== undefined
          ? String(typeCurveData.fluidIntensityGalFt)
          : '',
      fluidIntensityBbl:
        typeCurveData.fluidIntensityBblFt !== null &&
        typeCurveData.fluidIntensityBblFt !== undefined
          ? String(typeCurveData.fluidIntensityBblFt)
          : '',
      vintage: typeCurveData.vintageDate ?? '',
      operators: typeCurveData.operatorNames ?? [],
      developmentType: typeCurveData.developmentType
        ? [typeCurveData.developmentType]
        : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTcWorkspace?.id, typeCurveData?.id, setDraft]);

  // When the user resolves the last overlap, automatically verify the "Resolve unit overlaps" task.
  const prevOverlapCountRef = useRef(0);
  useEffect(() => {
    const hadOverlaps = prevOverlapCountRef.current > 0;
    prevOverlapCountRef.current = overlappingUnits.length;

    const workspace = activeTcWorkspace;
    if (
      overlappingUnits.length > 0 ||
      !hadOverlaps ||
      !workspace ||
      workspace.promoted ||
      getTcWorkspaceUnitResolutionVerification(workspace)?.status
    ) {
      return;
    }

    // Last overlap resolved = # overlaps changed from >0 to 0.
    const eventsHref = tcWorkspaceUnitResolutionEventsHref(workspace);
    if (!eventsHref) {
      return;
    }
    submitVerificationEvent({
      url: eventsHref,
      type: 'POST',
      data: {
        kind: 'verification_correct',
        reason:
          'Automatically marked correct: all unit overlaps resolved by user',
        details: null,
      },
      onSuccess: async () => {
        const selfHref = tcWorkspaceSelfHref(workspace);
        if (!selfHref) {
          return;
        }
        const fresh = await getReq<TypeCurveWorkspaceResponse>(selfHref);
        if (fresh && typeof fresh === 'object' && !('error' in fresh)) {
          setActiveTcWorkspace(fresh);
        }
      },
    });
  }, [
    overlappingUnits,
    activeTcWorkspace,
    submitVerificationEvent,
    setActiveTcWorkspace,
  ]);

  const handleAreaSaveSuccess = useCallback(() => {
    void refreshTypeCurveHeader();
    void queryClient.invalidateQueries({
      queryKey: [SAVED_WELL_FILTERS_KEY],
    });
  }, [refreshTypeCurveHeader, queryClient]);

  if (error) {
    return (
      <Layer>
        <Tile>
          <EmptyState icon={DevAreaSvg} headerText="Type curve not found" />
        </Tile>
      </Layer>
    );
  }

  if (loading) {
    return <Loading />;
  }

  if (!typeCurveData) {
    return (
      <Layer>
        <Tile>
          <EmptyState icon={DevAreaSvg} headerText="Type curve not found" />
        </Tile>
      </Layer>
    );
  }

  if (!workspaceIdReady) {
    return <Loading />;
  }

  const typeCurveTitle = typeCurveData.uniqueTcId || 'Type Curve Details';
  const latestPromotedStatus =
    typeCurveData.effectiveVerification?.verificationStatus;
  const normalizedLatestPromotedStatus = String(
    latestPromotedStatus ?? ''
  ).toLowerCase();
  const showLatestPromotedStatus =
    normalizedLatestPromotedStatus === 'correct' ||
    normalizedLatestPromotedStatus === 'incorrect';
  const pageHeaderTitle = (
    <div className={style.typeCurveTitleWithStatus}>
      <h1>{typeCurveTitle}</h1>
      <TypeCurveStatusTag
        promoted={typeCurveData.promoted}
        isDefault={typeCurveData.isDefault}
        archived={typeCurveData.archived}
      />
      {showLatestPromotedStatus ? (
        <TypeCurveVerificationStatusIndicator
          status={latestPromotedStatus}
          showLabel
          hoverVerifiedBy
          markedBy={typeCurveData.effectiveVerification?.markedBy}
          markedAt={typeCurveData.effectiveVerification?.markedAt}
          tooltipPlacement="bottom"
          className={style.latestPromotedVerification}
          iconClasses={{
            correct: style.latestPromotedVerificationCorrect,
            incorrect: style.latestPromotedVerificationIncorrect,
          }}
        />
      ) : null}
      {typeCurveData.validity ? (
        <TypeCurveValidationCell
          id={typeCurveData.id}
          validity={typeCurveData.validity}
          className={style.headerValidation}
          iconOverride={
            typeCurveData.validity?.toLowerCase() === 'valid' ? (
              <CheckmarkFilled />
            ) : undefined
          }
        />
      ) : null}
    </div>
  );

  const handleLeaveWorkspace = () => {
    setRightPanel({ open: false, type: 'dca' });
    setActiveTcWorkspace(null);
    setPromoteModalOpen(false);
    setPromoteError(null);
    setPromoteReasons([]);
    void refreshTypeCurveHeader();
  };

  const handleOpenPromoteModal = () => {
    setPromoteError(null);
    setPromoteReasons([]);
    setSetAsDefault(false);
    setPromoteModalOpen(true);
  };

  const handlePromoteConfirm = () => {
    if (!activeTcWorkspace || promoteLoading) {
      return;
    }

    const workspaceUrl = tcWorkspaceSelfHref(activeTcWorkspace);

    if (!workspaceUrl) {
      setPromoteError(
        'Workspace URL is missing from the response. Please refresh and try again.'
      );
      return;
    }

    setPromoteError(null);
    setPromoteReasons([]);

    promoteWorkspace({
      type: 'PATCH',
      url: workspaceUrl,
      data: { workspace: { promote: true, setAsDefault } },
      onSuccess: (data) => {
        setActiveTcWorkspace(data);
        setPromoteModalOpen(false);
        setToast({
          kind: 'success',
          title: 'Verification promoted',
          subtitle: `Persisted to ${typeCurveTitle}.`,
        });
        void refreshWorkspaces();
      },
      onError: (error?: {
        errors?: {
          message?: string;
          details?: { incomplete?: string[] };
        }[];
        error?: string;
      }) => {
        const first = error?.errors?.[0];
        const reasons = first?.details?.incomplete ?? [];
        setPromoteReasons(reasons);
        setPromoteError(
          reasons.length
            ? 'Unable to promote. Please try again.'
            : first?.message || error?.error || 'Unable to promote workspace.'
        );
      },
    });
  };

  return (
    <div className={style.pageWrapper}>
      <EventsContent
        eventsHref={
          activeTcWorkspace
            ? `/type_curves/${id}/workspaces/${activeTcWorkspace.id}/events`
            : typeCurveHeaderEventsHref(typeCurveData)
        }
        commentsHref={
          activeTcWorkspace
            ? `/type_curves/${id}/workspaces/${activeTcWorkspace.id}/comments`
            : typeCurveHeaderCommentsHref(typeCurveData)
        }
        typeCurveName={typeCurveTitle}
      />
      <Helmet>
        <title>
          {pageTitle(
            activeTcWorkspace
              ? `${activeTcWorkspace.name} · ${typeCurveTitle}`
              : typeCurveTitle
          )}
        </title>
      </Helmet>

      {activeTcWorkspace ? (
        <TypeCurveWorkspaceHeader
          workspace={activeTcWorkspace}
          typeCurveLabel={typeCurveTitle}
          leftPanelOpen={leftOpen}
          onLeftPanelToggle={setLeftOpen}
          onBack={handleLeaveWorkspace}
          onPromote={handleOpenPromoteModal}
        />
      ) : (
        <PageHeader
          // stickyHeader
          title={pageHeaderTitle}
          compactContent
          breadcrumbs={[]}
          actions={
            <div className={style.actionsWrapper}>
              <SidebarToggle
                side="left"
                isOpen={leftOpen}
                onToggle={setLeftOpen}
                label="Panel"
              />
              <EventsPanelOpenButton />
              {typeCurveData.tcPublicId && (
                <ExportButtons tcPublicId={typeCurveData.tcPublicId} iconOnly />
              )}

              {typeCurveData.id ? (
                <WorkspacesMenu
                  typeCurveCount={workspaceListCount}
                  inventoryCount={inventoryWorkspaceListCount}
                  dcaCount={dcaWorkspaceListCount}
                  onOpen={(type: WorkspacesMenuType) =>
                    setRightPanel({ open: true, type })
                  }
                />
              ) : null}
              {typeCurveData.id && (
                <CreateMenu
                  typeCurveHeaderDetailsId={typeCurveData.id}
                  uwis={filteredUwis?.map((item) => item.uwi) ?? []}
                  dcaScenarioId={savedDcaScenarioId(selectedDcaScenario)}
                  canCreateTypeCurveWorkspace={Boolean(
                    workspacesCollectionHref
                  )}
                  onCreateTypeCurveWorkspace={() =>
                    setCreateTcWorkspaceModalOpen(true)
                  }
                  onCreateInventoryWorkspace={() =>
                    setCreateInventoryWorkspaceModalOpen(true)
                  }
                />
              )}
            </div>
          }
        />
      )}

      {(showWorkspaceIdleBanner || showDcaUnavailableBanner) && (
        <div className={style.pageBanners}>
          {showWorkspaceIdleBanner && (
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title="Create a Workspace to verify this Type Curve."
              subtitle="Inside the Workspace you can mark this Type Curve correct or incorrect and promote your verification to Insights. Past Workspaces stay on this page as a record of the work that has been completed."
            />
          )}
          {showDcaUnavailableBanner && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="DCA Parameters are not available for this Type Curve, please check the source."
            />
          )}
        </div>
      )}

      <DualSidebarLayout
        left={{
          content: (
            <TypeCurvePageFilters
              wellCount={wellsTotalCount || undefined}
              unitCount={unitsTotalCount || undefined}
              onEditColorRule={setEditingColorRuleId}
              onEditUnitColorRule={setEditingUnitColorRuleId}
              dcaScenarioName={selectedDcaScenario?.name}
              onEditDcaScenario={() =>
                setRightPanel({ type: 'dca', open: true })
              }
              typeCurveAreaName={detailsData?.typeCurveAreaName}
            />
          ),
          isOpen: leftOpen,
        }}
        right={{
          content: (
            <>
              {rightPanel.type === 'dca' ? (
                <DcaScenarioSelection
                  onClose={() => setRightPanel({ open: false, type: 'dca' })}
                />
              ) : null}
              {rightPanel.type === 'workspaces' && typeCurveData.id ? (
                <TcWorkspacesListPanel
                  listAtom={tcWorkspacesListAtom}
                  kind="type_curve"
                  hasCollection={Boolean(workspacesKindsHref)}
                />
              ) : null}
              {rightPanel.type === 'inventory-workspaces' &&
              typeCurveData.id ? (
                <TcWorkspacesListPanel
                  listAtom={tcWorkspacesListAtom}
                  kind="inventory"
                  title="Inventory Workspaces"
                  hasCollection={Boolean(workspacesKindsHref)}
                  onSelect={(ws) =>
                    window.open(
                      ws.href ?? `/inventory_workspaces/${ws.id}`,
                      '_blank'
                    )
                  }
                />
              ) : null}
              {rightPanel.type === 'dca-workspaces' && typeCurveData.id ? (
                <TcWorkspacesListPanel
                  listAtom={tcWorkspacesListAtom}
                  kind="dca"
                  title="DCA Workspaces"
                  hasCollection={Boolean(workspacesKindsHref)}
                  onSelect={(ws) =>
                    window.open(ws.href ?? `/dca_workspaces/${ws.id}`, '_blank')
                  }
                />
              ) : null}
              {rightPanel.type === 'verification' ? (
                <TcVerificationDecisionPanel kind={rightPanel.kind} />
              ) : null}
              {rightPanel.type === 'tasks' && activeTcWorkspace ? (
                <TcVerificationTasksPanel
                  workspace={activeTcWorkspace}
                  onClose={() => setRightPanel({ open: false, type: 'dca' })}
                  verifiedUnitsCount={
                    inventoryVerificationSummary?.verdictCount ?? 0
                  }
                  totalUnitsCount={
                    inventoryVerificationSummary?.totalUnits ?? 0
                  }
                  dcaParentId={typeCurveData?.id}
                  dcaScenarioId={savedDcaScenarioId(selectedDcaScenario)}
                  onHeaderChanged={refreshTypeCurveHeader}
                />
              ) : null}
              {rightPanel.type === 'unit-resolution' && activeTcWorkspace ? (
                <UnitResolutionPanel />
              ) : null}
              {rightPanel.type === 'inventory-guide' && activeTcWorkspace ? (
                <InventoryGuideModal />
              ) : null}
              {rightPanel.type === 'tc-area' && activeTcWorkspace ? (
                <TcAreaSelectorPanel
                  workspace={activeTcWorkspace}
                  currentTypeCurveAreaId={detailsData?.typeCurveAreaId ?? null}
                  onSaveSuccess={handleAreaSaveSuccess}
                />
              ) : null}
            </>
          ),
          isOpen:
            rightPanel.open &&
            (rightPanel.type !== 'tasks' || Boolean(activeTcWorkspace)),
        }}
      >
        {detailsData ? (
          <TypeCurveDetailsContent
            data={detailsData}
            id={id}
            onRefreshHeader={refreshTypeCurveHeader}
          />
        ) : (
          <Loading />
        )}
      </DualSidebarLayout>
      <TcWellFilterModal />
      <TcUnitFilterModal />
      <TcColorByModal
        editingRuleId={editingColorRuleId}
        onClose={() => setEditingColorRuleId(null)}
        typeCurveHeader={detailsData ?? null}
      />
      <TcUnitColorByModal
        editingRuleId={editingUnitColorRuleId}
        onClose={() => setEditingUnitColorRuleId(null)}
        typeCurveHeader={detailsData ?? null}
      />
      <TcCrossPlotChartModal />
      {typeCurveData.id && (
        <CreateInventoryWorkspaceModal
          open={createInventoryWorkspaceModalOpen}
          typeCurveHeaderDetailsId={typeCurveData.id}
          uniqueTcId={typeCurveData.uniqueTcId}
          typeCurveScenarioId={typeCurveData.typeCurveScenarioId}
          typeCurveAreaName={typeCurveData.typeCurveAreaName}
          onClose={() => setCreateInventoryWorkspaceModalOpen(false)}
        />
      )}
      {typeCurveData.id && (
        <CreateTypeCurveWorkspaceModal
          open={createTcWorkspaceModalOpen}
          workspacesCollectionHref={workspacesCollectionHref}
          sourceTypeCurveDetailId={typeCurveData.id}
          defaultScenarioId={typeCurveData.typeCurveScenarioId}
          onClose={() => setCreateTcWorkspaceModalOpen(false)}
          onCreated={(workspace) => {
            // The workspace opens in its own tab, so this page neither switches
            // into it nor refetches its workspaces list — that refetch raced the
            // create/auto-verify calls for a list the user is navigating away
            // from. Same as the listing: create, then just close.

            // If there are no unit overlaps on creation, automatically verify
            // the "Resolve unit overlaps" task.
            if (overlappingUnits.length > 0) {
              return;
            }
            const eventsHref = tcWorkspaceUnitResolutionEventsHref(workspace);
            if (!eventsHref) {
              return;
            }
            submitVerificationEvent({
              url: eventsHref,
              type: 'POST',
              data: {
                kind: 'verification_correct',
                reason:
                  'Automatically marked correct: workspace created with no unit overlaps',
                details: null,
              },
              onSuccess: async () => {
                // Pull the now-verified status into the active workspace.
                const selfHref = tcWorkspaceSelfHref(workspace);
                if (!selfHref) {
                  return;
                }
                const fresh =
                  await getReq<TypeCurveWorkspaceResponse>(selfHref);
                if (fresh && typeof fresh === 'object' && !('error' in fresh)) {
                  setActiveTcWorkspace(fresh);
                }
              },
            });
          }}
        />
      )}
      <TcPromoteWorkspaceModal
        open={promoteModalOpen && Boolean(activeTcWorkspace)}
        workspace={activeTcWorkspace}
        typeCurveLabel={typeCurveTitle}
        loading={promoteLoading}
        error={promoteError}
        errorReasons={promoteReasons}
        inventoryVerificationSummary={inventoryVerificationSummary}
        removedUnitsCount={removedUnitIds.size}
        setAsDefault={setAsDefault}
        onSetAsDefaultChange={setSetAsDefault}
        onClose={() => {
          if (!promoteLoading) {
            setPromoteModalOpen(false);
            setPromoteError(null);
            setPromoteReasons([]);
          }
        }}
        onConfirm={handlePromoteConfirm}
      />
    </div>
  );
};

export const EventsContent = ({
  typeCurveName,
  eventsHref,
  commentsHref,
}: {
  typeCurveName: string;
  eventsHref: string | null;
  commentsHref: string | null;
}) => {
  if (!eventsHref || !commentsHref) {
    return null;
  }

  return (
    <EventsPanel
      header={typeCurveName}
      title="Type Curve"
      helperText="Type Curve context allows you to share information about a type curve."
      eventsHref={eventsHref}
      commentsHref={commentsHref}
    />
  );
};

export default TypeCurveDetailsPage;
